import BigNumber from 'bignumber.js'
import { CLPool, CLPoolKey, Token } from '../../atoms'
import { BulkCall, bulkCallContract } from '../../blockchainUtils/bulkCall'
import { getAMMV3Contract } from '../../services/amm_v3'
import { Network } from '../../services/token.service'
import store from '../store'
import { getToken } from '../tokens/helper'

export const ALL_FEES = [10, 100, 500, 3000, 10000]
export const SPACING_PER_FEE: {
    10: number
    100: number
    500: number
    3000: number
    10000: number
    [key: number]: number // This line allows access using any number
} = {
    10: 1,
    100: 2,
    500: 10,
    3000: 60,
    10000: 200
}

export const FACTORY_ADDRESS = '0x07d811d049e222fb4f37207cd4f6f4801b0f9c1b797039e043bdb87a52138894'

export function getFactoryAddress(network: Network) {
    if (network === 'sepolia-alpha') return '0x02354515660599bf97a21a2de195785ef8f407399ad910b98f8d15683cde193c' // '0x07c6429f5802d903061f5546bb4a538b2d086975f7d492976197bc250a0240f9' // old factory: '0x046db5f1e0a251c5a3106d06c1535a65088d49355a62de40376e35ad761e68e7'
    if (network === 'goerli-alpha') return '0x436924c4ed166d3c283d516adc424976cfccba108e3a0e3f3fc1ef319e23aa7' // '0x07c6429f5802d903061f5546bb4a538b2d086975f7d492976197bc250a0240f9' // old factory: '0x046db5f1e0a251c5a3106d06c1535a65088d49355a62de40376e35ad761e68e7'
    if (network === 'mainnet-alpha') return '0x1114c7103e12c2b2ecbd3a2472ba9c48ddcbf702b1c242dd570057e26212111'
    return '0x07d811d049e222fb4f37207cd4f6f4801b0f9c1b797039e043bdb87a52138894'
}

export const poolKeyToString = (k: CLPoolKey): string => {
    const d1 = BigInt(k.token1).toString(10)
    const d2 = BigInt(k.token2).toString(10)
    if (d1 < d2) {
        return `${d1.toString()}_${d2.toString()}_${k.fee}`
    }
    return `${d2.toString()}_${d1.toString()}_${k.fee}`
}

export const getCLPool = (network: Network, token1: Token, token2: Token, fee: number): CLPool | undefined => {
    if (token1 === undefined || token2 === undefined) return undefined
    const x = store.getState().clPools.pools[network]
    return x
        ? x[
              poolKeyToString({
                  token1: token1.address,
                  token2: token2.address,
                  fee: fee
              })
          ]
        : undefined
}

export const getAllCLPools = async (network: Network, token1: Token, token2: Token, fees?: number[]): Promise<Array<CLPool>> => {
    const feeList = fees ? fees : ALL_FEES
    const arr: any[] = []
    feeList.forEach(async (fee) => {
        arr.push(
            store.getActions().clPools.discoverPool({
                network,
                token1,
                token2,
                fee
            })
        )
    })
    try {
        const responses = await Promise.all(arr)
        return responses
    } catch (e: any) {
        console.log('error ', e.message)
        return []
    }
}

export const getAllCLPoolForFee = async (network: Network, token1: Token, token2: Token, fee: number): Promise<CLPool> => {
    const clPoolsStore = store.getState().clPools
    const discoveredPool = clPoolsStore.getClPool(network, token1.address, token2.address, fee)
    if (discoveredPool) return discoveredPool
    try {
        await store.getActions().clPools.discoverPool({
            network,
            token1,
            token2,
            fee
        })
    } catch (e) {
        console.log('error=', e)
    }

    const pool = store.getState().clPools.pools[network]
    return pool[
        poolKeyToString({
            token1: token1.address,
            token2: token2.address,
            fee: fee
        })
    ]
    // })
    // .filter((x) => x)
}

export function clPoolsArrayToRecord(pools: CLPool[]) {
    const poolRecord: Record<string, CLPool> = {}
    pools.map((pool) => {
        poolRecord[
            poolKeyToString({
                token1: pool.token1.address,
                token2: pool.token2.address,
                fee: pool.fee
            })
        ] = pool
    })
    return poolRecord
}

const toUint256 = (low: string, high: string): BigNumber => {
    const l = new BigNumber(low)
    const h = new BigNumber(high)

    return h.multipliedBy(new BigNumber(2).pow(128)).plus(l)
}
const toX96 = (x: BigNumber): BigNumber => {
    return x.dividedBy(new BigNumber(2).pow(96))
}

export async function fetchPoolPrice(network: Network, pool: CLPool): Promise<CLPool> {
    const calls: BulkCall[] = []
    const amm = getAMMV3Contract(network)
    calls.push({
        call: {
            contractAddress: amm,
            entrypoint: 'current_tick',
            calldata: [pool.address]
        },
        responseParser: {
            kind: 'fixed',
            length: 1
        }
    })
    calls.push({
        call: {
            contractAddress: amm,
            entrypoint: 'current_sqrt_price',
            calldata: [pool.address]
        },
        responseParser: {
            kind: 'fixed',
            length: 2
        }
    })
    ;(await bulkCallContract(network, calls)).map((res, index) => {
        if (index == 0) {
            // tick
            pool.currentTick = Number(BigInt(res.result[0])) - 887272
        }
        if (index == 1) {
            // price
            pool.currentPrice = toX96(toUint256(res.result[0], res.result[1])).toString()
        }
    })

    return pool
}

export async function fetchPoolsFromFactory(network: Network, candidates: Array<CLPoolKey>): Promise<Array<CLPool>> {
    const calls: BulkCall[] = candidates.map((k) => {
        console.log('fetchPoolsFromFactory k ', k)
        const b: BulkCall = {
            call: {
                contractAddress: getFactoryAddress(network),
                entrypoint: 'get_pool',
                calldata: [k.token1, k.token2, k.fee]
            },
            responseParser: {
                kind: 'fixed',
                length: 1
            }
        }
        return b
    })

    const pools: Array<CLPool> = []
    // console.log('YSYS bulk=', calls)
    ;(await bulkCallContract(network, calls)).forEach((res, index) => {
        console.log('YSYS fetchPoolsFromFactory bulkCallContract res=', res, ' index=', index)
        const address = BigInt(res.result[0])
        // console.log('YSYS address=', address)
        if (address.toString() !== '0') {
            // this is a valid pool
            // console.log('YSYS valid pool!')
            const pkey = candidates[index]
            // we put the user's token order here,
            // BUT WE WILL FIX THE ORDER before pushing into redux
            // as this order is not neccessarily the pool order on chain
            const token1 = getToken(network, pkey.token1)
            const token2 = getToken(network, pkey.token2)
            if (token1 && token2) {
                const pool: CLPool = {
                    address: res.result[0],
                    token1: token1,
                    token2: token2,
                    fee: pkey.fee,
                    tickSpacing: SPACING_PER_FEE[pkey.fee],
                    currentPrice: undefined,
                    currentTick: undefined
                }
                pools.push(pool)
            }
        }
    })

    // now we fix the pool order
    const poolsWithCorrectTokenOrder: Array<CLPool> = []
    const calls2: BulkCall[] = pools.map((k) => {
        const b: BulkCall = {
            call: {
                contractAddress: getFactoryAddress(network),
                entrypoint: 'token0',
                calldata: [k.address]
            },
            responseParser: {
                kind: 'fixed',
                length: 1
            }
        }
        return b
    })

    // console.log('YSYS bulk=', calls2)
    ;(await bulkCallContract(network, calls2)).forEach((res, index) => {
        // console.log('YSYS res=', res, ' index=', index)
        const first_token_address = BigInt(res.result[0]).toString()
        if (first_token_address !== '0') {
            // this is a valid token
            const pool = pools[index]
            const pool_first_token_address = BigInt(pool.token1.address).toString()
            // console.log('YSYS first_token_address=', first_token_address)
            // console.log('YSYS pool struct token1_address=', pool_first_token_address)

            // check if we need to fix the order
            if (pool_first_token_address === first_token_address) {
                // console.log('YSYS : Pool is in correct order')
            } else {
                // console.log('YSYS : Pool is NOT in correct order')
                const t2 = pool.token2
                pool.token2 = pool.token1
                pool.token1 = t2
            }
            poolsWithCorrectTokenOrder.push(pool)
        }
    })

    // console.log('YSYS returning discoverd pools ', poolsWithCorrectTokenOrder)
    return poolsWithCorrectTokenOrder
}

export function getIsPoolTokensReversed(pool: CLPool, token0Address: string, token1Address: string) {
    if (pool.token1.address === token0Address && pool.token2.address === token1Address) {
        return false
    }
    if (pool.token1.address === token1Address && pool.token2.address === token0Address) {
        return true
    }

    return false
}
